import { FC, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
	ButtonGroup,
	DialogTitle,
	PunchupDialogue,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupConfirmDialog } from 'features/common/components/Punchup/composites/dialogs/PunchupConfirmDialog';
import { getOrphanedSeats, remapOrphanedSeats } from '../services';
import { PunchupBuyerSeatsWidget } from 'features/common/components/Punchup/composites/seats/PunchupBuyerSeatsWidget';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { SeatRemapAction, SeatRemapError, SeatRemapMapping, SeatsSyncEvent } from '../types';
import { useToastNotification } from 'hooks/useToastNotification';
import styles from './ReassignSeatsDialog.module.css';

interface ReassignSeatsDialogProps {
	event: SeatsSyncEvent;
	onClose: () => void;
}

const seatLabel = (object: { label?: string | number }): string | null =>
	object.label === undefined || object.label === null ? null : String(object.label);

const extractMappingErrors = (error: unknown): SeatRemapError[] =>
	(error as { response?: { data?: { errors?: SeatRemapError[] } } })?.response?.data?.errors ?? [];

export const ReassignSeatsDialog: FC<ReassignSeatsDialogProps> = ({ event, onClose }) => {
	const { t } = useTranslation();
	const { showNotification, TYPES } = useToastNotification();
	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery({
		queryKey: ['orphaned-seats', event.event_id],
		queryFn: () => getOrphanedSeats(event.event_id),
	});
	const orphaned = data?.orphaned_tickets ?? [];

	const [selected, setSelected] = useState<string[]>([]);
	const [plan, setPlan] = useState<SeatRemapAction[] | null>(null);
	const [planErrors, setPlanErrors] = useState<SeatRemapError[]>([]);
	const [confirming, setConfirming] = useState(false);

	// Stranded tickets come sorted by old label; replacements pair up in the
	// order the operator clicks them on the chart.
	const mappings: SeatRemapMapping[] = useMemo(
		() =>
			orphaned
				.slice(0, selected.length)
				.map((ticket, index) => ({ ticket_id: ticket.ticket_id, new_seat: selected[index] })),
		[orphaned, selected]
	);
	const ready = orphaned.length > 0 && selected.length === orphaned.length;
	// A stranded ticket whose stale label maps 1:1 to a current-chart seat (a
	// category rename that only shortened the label) can be filled without
	// hunting on the chart — which matters because those twins are booked on the
	// chart and therefore not clickable there.
	const allSuggested = orphaned.length > 0 && orphaned.every((ticket) => !!ticket.suggested_seat);
	const suggestionsApplied =
		allSuggested && orphaned.every((ticket, index) => selected[index] === ticket.suggested_seat);

	const invalidatePlan = () => {
		setPlan(null);
		setPlanErrors([]);
	};

	const applySuggestions = () => {
		invalidatePlan();
		setSelected(orphaned.map((ticket) => ticket.suggested_seat ?? ''));
	};

	const preview = useMutation({
		mutationFn: () => remapOrphanedSeats(event.event_id, mappings, false),
		onSuccess: (response) => {
			setPlan(response.remapped);
			setPlanErrors([]);
		},
		onError: (error) => {
			const errors = extractMappingErrors(error);
			setPlan(null);
			setPlanErrors(errors);
			if (!errors.length) {
				showNotification({
					message: t('seatsSync-reassign-previewError', 'Failed to preview the reassignment'),
					type: TYPES.ERROR,
					error,
				});
			}
		},
	});

	const apply = useMutation({
		mutationFn: () => remapOrphanedSeats(event.event_id, mappings, true),
		onSuccess: () => {
			showNotification({
				message: t('seatsSync-reassign-success', 'Tickets moved to their new seats'),
				type: TYPES.SUCCESS,
			});
			queryClient.invalidateQueries({ queryKey: ['seats-sync-status'] });
			queryClient.invalidateQueries({ queryKey: ['orphaned-seats', event.event_id] });
			onClose();
		},
		onError: (error) => {
			const errors = extractMappingErrors(error);
			setConfirming(false);
			setPlan(null);
			setPlanErrors(errors);
			if (!errors.length) {
				showNotification({
					message: t('seatsSync-reassign-error', 'Failed to reassign the seats'),
					type: TYPES.ERROR,
					error,
				});
			}
		},
	});

	const handleObjectSelected = (object: { label?: string | number }) => {
		const label = seatLabel(object);
		if (!label) return;
		invalidatePlan();
		setSelected((current) => (current.includes(label) ? current : [...current, label]));
	};

	const handleObjectDeselected = (object: { label?: string | number }) => {
		const label = seatLabel(object);
		if (!label) return;
		invalidatePlan();
		setSelected((current) => current.filter((seat) => seat !== label));
	};

	const isMutating = preview.isLoading || apply.isLoading;

	return (
		<PunchupDialogue maxWidth={900} onClose={onClose}>
			<DialogTitle>
				{t('seatsSync-reassign-title', 'Reassign stranded seats')} — {event.event_name}
			</DialogTitle>

			{isLoading && <PunchupSpinner />}

			{!isLoading && orphaned.length === 0 && (
				<>
					<p className={styles.empty}>
						{t('seatsSync-reassign-none', 'No stranded tickets on this event — every sold seat exists on the chart.')}
					</p>
					<ButtonGroup>
						<PunchupButton variant="outline" onClick={onClose}>
							{t('seatsSync-reassign-close', 'Close')}
						</PunchupButton>
					</ButtonGroup>
				</>
			)}

			{!isLoading && data && orphaned.length > 0 && (
				<>
					<p className={styles.instructions}>
						{t(
							'seatsSync-reassign-instructions',
							'These sold seats no longer exist on the chart. Where a category rename only shortened a label, its renamed seat is suggested — apply the suggestions below. Otherwise pick a replacement on the chart, in the order listed. Nothing changes until you confirm.'
						)}
					</p>

					{allSuggested && (
						<div className={styles.suggestBar}>
							<PunchupButton variant="outline" onClick={applySuggestions} disabled={isMutating || suggestionsApplied}>
								{suggestionsApplied
									? t('seatsSync-reassign-suggestionsApplied', 'Suggestions applied')
									: `${t('seatsSync-reassign-useSuggestions', 'Use suggested seats')} (${orphaned.length})`}
							</PunchupButton>
							<span className={styles.suggestHint}>
								{t(
									'seatsSync-reassign-suggestHint',
									'Each stale label maps to its renamed seat, already booked on the chart — applying re-points the ticket in place.'
								)}
							</span>
						</div>
					)}

					<ul className={styles.pairings}>
						{orphaned.map((ticket, index) => (
							<li key={ticket.ticket_id} className={styles.pairing}>
								<span>
									{ticket.seat_label} · {ticket.ticket_type_name} · #{ticket.ticket_id}
								</span>
								<span className={styles.pairingTarget}>
									→{' '}
									{selected[index] ??
										(ticket.suggested_seat
											? `${ticket.suggested_seat} ${t('seatsSync-reassign-suggestedTag', '(suggested)')}`
											: t('seatsSync-reassign-pending', 'pick a seat'))}
								</span>
							</li>
						))}
					</ul>

					<div className={styles.chart}>
						<PunchupBuyerSeatsWidget
							eventKey={data.seats_event_key}
							workspaceKey={data.seats_workspace_key}
							seatsRegion={data.seats_region}
							session="none"
							maxSelectedObjects={orphaned.length}
							chartProps={{
								onChartRendered: () => undefined,
								onObjectSelected: handleObjectSelected,
								onObjectDeselected: handleObjectDeselected,
							}}
						/>
					</div>

					{planErrors.length > 0 && (
						<ul className={styles.errors}>
							{planErrors.map((mappingError) => (
								<li key={`${mappingError.ticket_id}-${mappingError.new_seat}`}>
									{mappingError.new_seat ? `${mappingError.new_seat}: ` : ''}
									{mappingError.error}
								</li>
							))}
						</ul>
					)}

					{plan && (
						<ul className={styles.plan}>
							{plan.map((action) => (
								<li key={action.ticket_id}>
									{action.old_seat} → {action.new_seat}
									{action.relabel ? ` ${t('seatsSync-reassign-relabelTag', '(relabel)')}` : ''}
									{action.new_ticket_type_id !== action.old_ticket_type_id ? ` (${action.new_ticket_type_name})` : ''}
								</li>
							))}
						</ul>
					)}

					<ButtonGroup>
						<PunchupButton variant="outline" onClick={onClose} disabled={isMutating}>
							{t('seatsSync-reassign-cancel', 'Cancel')}
						</PunchupButton>
						{!plan && (
							<PunchupButton
								disabled={!ready || isMutating}
								isLoading={preview.isLoading}
								onClick={() => preview.mutate()}
							>
								{t('seatsSync-reassign-preview', 'Preview')}
							</PunchupButton>
						)}
						{plan && (
							<PunchupButton disabled={isMutating} onClick={() => setConfirming(true)}>
								{t('seatsSync-reassign-confirm', 'Confirm reassignment')}
							</PunchupButton>
						)}
					</ButtonGroup>
				</>
			)}

			{confirming && plan && (
				<PunchupConfirmDialog
					iconVariant="destructive"
					title={t('seatsSync-reassign-confirmTitle', 'Move these sold tickets?')}
					text={`${plan.length} ${t(
						'seatsSync-reassign-confirmText',
						'ticket(s) will move to new seats on the live event. Buyers keep their price — only the seat and ticket type change, and this cannot be undone from here.'
					)}`}
					secondaryText={plan
						.map(
							(action) =>
								`${action.old_seat} → ${action.new_seat}${action.relabel ? ' (relabel)' : ''}${
									action.new_ticket_type_id !== action.old_ticket_type_id ? ` (${action.new_ticket_type_name})` : ''
								}`
						)
						.join('   ·   ')}
					isLoading={apply.isLoading}
					isDestructive
					buttons={[
						{
							label: t('seatsSync-reassign-confirmCancel', 'Go back'),
							variant: 'secondary',
							onClick: () => setConfirming(false),
						},
						{
							label: t('seatsSync-reassign-confirmApply', 'Yes, move the tickets'),
							variant: 'primary',
							onClick: () => apply.mutate(),
						},
					]}
				/>
			)}
		</PunchupDialogue>
	);
};
